import { createTheme } from '@mui/material/styles';

import { colors, radii } from '@/components/landing/landingTokens';

/**
 * The DESIGN.md §7 radius scale, in `px` strings. Module scope so that
 * `custom.radii` and the per-component overrides at the bottom of this file
 * spend one source rather than two that can drift.
 */
const radiiPx = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  pill: '9999px',
} as const;

/**
 * DeepThink app-wide MUI theme. Encodes tokens from DESIGN.md §4/§5/§7/§14.
 *
 * Palette strategy (Decision #1 from /plan-eng-review, 2026-04-18):
 * `primary` and `secondary` keep their historical values (cobalt / orange) so
 * existing MUI Button callsites don't silently re-skin. New `cta` (orange) and
 * `selection` (cobalt) slots carry the DESIGN.md roles. PushButton consumes
 * `palette.cta` / `palette.selection`. Per-callsite classification happens
 * explicitly during screen-rebuild PRs.
 *
 * MuiButton override stays minimal — radius, weight, touch-target only. The
 * pushable bottom-shadow + translateY lives in the <PushButton> component so
 * Workspace and Marketing modes (DESIGN.md §3) keep their own density.
 */
export const theme = createTheme({
  custom: {
    gradients: {
      primary: `linear-gradient(90deg, ${colors.accentCobalt}, ${colors.accentCobaltDark})`,
      cta: `linear-gradient(135deg, ${colors.accentCobalt} 0%, ${colors.accentCobaltBright} 40%, ${colors.accentOrange} 100%)`,
      accent: `linear-gradient(90deg, ${colors.accentOrange}, ${colors.accentAmber})`,
      surface:
        'linear-gradient(135deg, rgba(245,247,252,0.95) 0%, rgba(237,240,249,0.95) 50%, rgba(226,230,242,0.95) 100%)',
    },
    shadows: {
      soft: '0 2px 6px rgba(12, 18, 45, 0.06)',
      lift: '0 4px 14px rgba(12, 18, 45, 0.08)',
      hero: '0 12px 32px rgba(12, 18, 45, 0.10)',
    },
    // `px` strings, not numbers — a numeric `borderRadius` in `sx` is a
    // multiplier of `theme.shape.borderRadius`, not pixels. See the note on
    // `radii` in `landingTokens.ts`.
    radii: radiiPx,
  },
  palette: {
    primary: {
      main: colors.accentCobalt,
      dark: '#2A3EB1',
      light: '#5A76E4',
    },
    secondary: {
      main: colors.accentOrange,
      dark: '#B84A1C',
      light: '#FF8A5C',
    },
    cta: {
      main: colors.accentOrange,
      dark: '#B84A1C',
      light: '#FF8A5C',
      contrastText: '#FFFFFF',
    },
    selection: {
      main: colors.accentCobalt,
      dark: '#2A3EB1',
      light: '#5A76E4',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#2EA366',
      dark: '#1E7A4A',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D63847',
      dark: '#9B2832',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: colors.accentAmber,
      dark: '#D18B63',
      contrastText: '#1A1E30',
    },
    // §4: cobalt owns the informational role — MUI info surfaces (Alerts,
    // chips) ride the brand cobalt, not MUI's default #0288d1 blue.
    info: {
      main: colors.accentCobalt,
      dark: '#2A3EB1',
      light: '#5A76E4',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: colors.ink,
      secondary: colors.inkMuted,
      disabled: colors.inkSubtle,
    },
    background: {
      default: colors.background,
      paper: colors.paper,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: "'Sora', 'Sora Fallback', sans-serif",
    h1: {
      fontWeight: 800,
      fontSize: '2rem',
      lineHeight: 1.08,
      letterSpacing: '-0.025em',
      '@media (min-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontWeight: 800,
      fontSize: '1.5rem',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontWeight: 800,
      fontSize: '1.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.018em',
      '@media (min-width:600px)': {
        fontSize: '1.375rem',
      },
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      '@media (min-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: 1.3,
      '@media (min-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    h6: {
      fontWeight: 700,
      fontSize: '0.9375rem',
      lineHeight: 1.3,
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.55,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      textTransform: 'none',
    },
    caption: {
      fontWeight: 600,
      fontSize: '0.75rem',
      lineHeight: 1.35,
    },
    overline: {
      fontWeight: 800,
      fontSize: '0.6875rem',
      lineHeight: 1.3,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  // The `sx` UNIT, and nothing else. MUI spends `shape.borderRadius` two ways:
  // as the default corner on its own components, and as the number a numeric
  // `sx.borderRadius` multiplies. Only the second job is still live here —
  // every MUI component that used to take its corner from `shape` now carries
  // an explicit DESIGN.md §7 override in `components` below, so no rendered
  // corner traces back to this value.
  //
  // It stays 4 because ~334 call sites across the product write
  // `borderRadius: 2` meaning "two units", and every one of them was tuned by
  // eye against MUI's default 4 — `buildBrandedTheme` used to drop `shape`
  // entirely, so the 12 originally declared here never reached a pixel. Reach
  // for `custom.radii` for anything that means a specific radius; this number
  // means one step.
  shape: {
    borderRadius: 4,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        // KaTeX emits a 1×1, clipped, `position: absolute` MathML copy of
        // every expression (`.katex-mathml`) for screen readers and
        // copy-paste. `.katex` is the one KaTeX wrapper its own stylesheet
        // leaves unpositioned, so that span's containing block falls all the
        // way through to the initial containing block — which means an
        // ancestor scroll container (a TableContainer, a drawer) CANNOT clip
        // it. It then drags the document's scrollWidth out to wherever the
        // span happens to land, and the whole page grows a phantom
        // horizontal scrollbar pointing at empty space.
        //
        // Safe because every OTHER absolutely-positioned KaTeX part already
        // sits inside its own relatively-positioned ancestor — `.tag` under
        // `.katex-html`, the laps under `.clap/.llap/.rlap`, the brace and
        // half-arrow pieces under `.stretchy`, the cd-labels under
        // `.cd-vert-arrow`. `.katex-mathml` is the only orphan, so this
        // re-parents it and nothing else, and changes nothing visible.
        '.katex': {
          position: 'relative',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width:600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: '48px',
          fontSize: '0.875rem',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          borderRadius: radii.md,
          textTransform: 'none',
          '@media (min-width:900px)': {
            minHeight: '44px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: t.custom.radii.lg,
          boxShadow: t.custom.shadows.soft,
          border: `1px solid ${t.palette.divider}`,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: ({ theme: t }) => ({
          borderRadius: t.custom.radii.lg,
        }),
      },
    },

    // ─── DESIGN.md §7 corners, per component ────────────────────────────────
    // Everything below defaults its corner to `shape.borderRadius`, and §7
    // assigns radius by component ROLE — inputs 8, controls 12, cards 16 — so
    // a single scalar cannot express it. Each one takes its step explicitly;
    // that is what leaves `shape` free to be the `sx` unit alone.
    //
    // `styleOverrides` are appended after MUI's own styles, so a flat
    // declaration beats a flat base. Where MUI puts its radius behind a
    // selector, the override has to mirror that selector — see MuiAccordion.
    MuiOutlinedInput: {
      styleOverrides: {
        // NotchedOutline is `borderRadius: inherit`, so the root carries it.
        root: {
          borderRadius: radiiPx.sm,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        // Top two corners only. The filled variant sits on an underline, and a
        // flat `borderRadius` would round that bottom edge away.
        root: {
          borderTopLeftRadius: radiiPx.sm,
          borderTopRightRadius: radiiPx.sm,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        // The `outlined` slot, not `select`: only the variant slot gets wrapped
        // in `&.MuiSelect-select` by MUI's resolver, and that extra class is
        // what out-orders the base `&:focus` Chrome reset, which writes a
        // radius of its own.
        outlined: {
          borderRadius: radiiPx.sm,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: radiiPx.sm,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        // The corner belongs on the button, never on the group's `grouped` /
        // `firstButton` / `lastButton` slots: those land at the same
        // specificity as MUI's inner-corner zeroing but later, so a radius
        // there would re-round a segmented control into separate pills.
        root: {
          borderRadius: radiiPx.md,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: radiiPx.md,
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        // Matches MuiButton's 16px — the root's radius shapes the contained
        // variant's shadow, the buttons draw the visible corners.
        root: {
          borderRadius: radiiPx.lg,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        // MUI zeroes every corner and re-rounds only a stack's outer edges,
        // through `:first-of-type` / `:last-of-type`. Those out-specify a flat
        // `borderRadius`, so the override mirrors them. Do not "simplify" this
        // to a shorthand: that loses the visible outer corners AND wrongly
        // rounds the middle panels of a stack.
        rounded: {
          '&:first-of-type': {
            borderTopLeftRadius: radiiPx.lg,
            borderTopRightRadius: radiiPx.lg,
          },
          '&:last-of-type': {
            borderBottomLeftRadius: radiiPx.lg,
            borderBottomRightRadius: radiiPx.lg,
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        // Matches `<Snackbar><Alert/></Snackbar>`, which already draws Paper's 16px.
        root: {
          borderRadius: radiiPx.lg,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        // `text` is deliberately left on `shape`: MUI derives an
        // `Npx / N·1.67px` ellipse from it, and a fraction of the base unit is
        // the right semantic for a text-line placeholder.
        rounded: {
          borderRadius: radiiPx.md,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: radiiPx.sm,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        rounded: {
          borderRadius: radiiPx.sm,
        },
      },
    },
  },
});
